/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useIntl } from 'react-intl-next';

import { jsx } from '@atlaskit/css';
import StatusDiscoveryIcon from '@atlaskit/icon/core/status-discovery';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import StatusInfoIcon from '@atlaskit/icon/core/status-information';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import TaskToDo from '@atlaskit/icon/core/task-to-do';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import messages from './messages';

export type LoadingStatus =
	| 'loading'
	| 'success'
	| 'error'
	| 'info'
	| 'warning'
	| 'discovery'
	| 'to-do';

export const StatusIndicator = ({ status, testId }: { status: LoadingStatus; testId?: string }) => {
	const { formatMessage } = useIntl();

	switch (status) {
		case 'loading':
			return <Spinner size="small" testId={testId} interactionName="loading-status-indicator-spinner" />;
		case 'success':
			return (
				<StatusSuccessIcon
					label={formatMessage(messages.success)}
					size="medium"
					color={token('color.icon.success')}
					testId={testId}
				/>
			);
		case 'info':
			return (
				<StatusInfoIcon
					label={formatMessage(messages.info)}
					size="medium"
					color={token('color.icon.information')}
					testId={testId}
				/>
			);
		case 'error':
			return (
				<StatusErrorIcon
					label={formatMessage(messages.error)}
					size="medium"
					color={token('color.icon.danger')}
					testId={testId}
				/>
			);
		case 'warning':
			return (
				<StatusWarningIcon
					label={formatMessage(messages.warning)}
					size="medium"
					color={token('color.icon.warning')}
					testId={testId}
				/>
			);
		case 'discovery':
			return (
				<StatusDiscoveryIcon
					label={formatMessage(messages.discovery)}
					size="medium"
					color={token('color.icon.discovery')}
					testId={testId}
				/>
			);
		case 'to-do':
			return <TaskToDo label={formatMessage(messages.todo)} size="medium" testId={testId} />;
		// explicitly return nothing from default case.
		// This shouldn't happen since `status` is required, but it could if someone casts incorrectly or data is undefined from the server.
		default:
	}
};
